import React, { useState, useEffect, useRef } from "react";
import {
	utcToLocalDateTime,
	utcToDate,
	BLOCK_REASON_REQUIRED_TEXT,
	isBlockReasonValid,
	normalizeBlockReason,
} from "../../components/helper";
import { useTranslation } from "../../contexts/TranslationContext";
import "./AllProfiles.scss";
import {
	getPhotos,
	deletePhoto,
	deleteSelfie,
	getDellUsers,
	blockUser,
	getVideo,
	deleteVideo,
	getSelfie,
	recomputeFaceMatch,
} from "../../api/apiServiceFetch";
import ConfirmPopup from "../../components/ConfirmPopup";
import Popup from "../../components/Popup";
import PhotoModal from "../../components/PhotoModal";
import { useAuth } from "../../contexts/auth";
import { getFileBaseUrl } from "../../config/environment";

const ITEMS_PER_PAGE = 30;
const DellProfiles = () => {
	const { userBase } = useAuth();
	const { t } = useTranslation();
	const [isPopupOpen, setIsPopupOpen] = useState(false);
	const [userButtonData, setUserButtonData] = useState(null);
	const [editUser, setEditUser] = useState();
	const [isConfirmPopupOpen, setIsConfirmPopupOpen] = useState(false);
	const [bigger, setBigger] = useState(false);
	const [users, setUsers] = useState([]);
	const [pageNumber, setPageNumber] = useState(1);
	const [expandedUserId, setExpandedUserId] = useState(null);
	const [videoUrl, setVideoUrl] = useState("");
	const [videoId, setVideoId] = useState();
	const videoRef = useRef(null);
	const [isPlaying, setIsPlaying] = useState(false);
	const [isMuted, setIsMuted] = useState(true);

	const [filterTerm, setFilterTerm] = useState("");
	const [totalPages, setTotalPages] = useState(1);
	const [filterCountry, setFilterCountry] = useState("");
	const [filterCity, setFilterCity] = useState("");
	const [filterSex, setFilterSex] = useState(0);
	const [userPhotos, setUserPhotos] = useState([]);
	const [userSelfie, setUserSelfie] = useState();
	const [faceMatchBusy, setFaceMatchBusy] = useState(false);

	const [reason, setReason] = useState("");
	const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
	const modalRef = useRef(null);
	const [loading, setLoading] = useState(true);
	const [showModal, setShowModal] = useState(false);
	const [showBlock, setShowBlock] = useState(false);
	const [userPhoto, setUserPhoto] = useState();

	const filePath = getFileBaseUrl();
	const fetchIdRef = useRef(0);

	const fetchUsers = async () => {
		const fetchId = ++fetchIdRef.current;
		try {
			const users = await getDellUsers(
				pageNumber,
				ITEMS_PER_PAGE,
				filterTerm,
				filterCountry,
				filterCity,
				filterSex,
			);

			if (fetchId !== fetchIdRef.current) return; // stale response — a newer fetch owns the list

			if (users.data.length === 0) {
				setUsers([]);
				setTotalPages(1);
				if (pageNumber > 1) setPageNumber(1); // page went out of range — back to the first page
			} else {
				setUsers(users.data);
				const totalPages = Math.ceil(
					users.data[0].totalRecords / ITEMS_PER_PAGE,
				);
				setTotalPages(totalPages < 1 ? 1 : totalPages);
			}
		} catch (error) {
			if (fetchId !== fetchIdRef.current) return;
			setUsers([]);
			console.error("Error fetching users by IDs:", error);
		} finally {
			if (fetchId === fetchIdRef.current) setLoading(false);
		}
	};

	const handleViewPhoto = (src) => {
		try {
			setUserPhoto(src);
			setShowModal(true);
		} catch (error) {
			console.error("Error fetching photos:", error);
		}
	};

	const fetchUserVideo = async (userId) => {
		try {
			const videoData = await getVideo(userId);

			if (videoData && videoData.fileName) {
				const videoUrl = `${filePath}/Video/${userId}/${videoData.fileName}`;
				setVideoId(videoData.id);
				setVideoUrl(videoUrl); // Set video URL in state
			}
		} catch (error) {
			console.error("Failed to fetch video:", error);
		}
	};

	const fetchUserPhotos = async (userId) => {
		try {
			const resp = await getPhotos(userId);
			if (resp) {
				setUserPhotos(resp);
			}
		} catch (error) {
			console.error("Failed to fetch video:", error);
		}
	};

	const fetchUserSelfie = async (userId) => {
		try {
			const resp = await getSelfie(userId);
			// Only keep a selfie when it actually has a file — the API returns an
			// empty object when there's none, which otherwise renders as
			// .../Selfie/{id}/undefined.
			setUserSelfie(resp && resp.fileName ? resp : undefined);
		} catch (error) {
			console.error("Failed to fetch selfie:", error);
			setUserSelfie(undefined);
		}
	};
	const handleRecomputeFaceMatch = async (userId) => {
		if (!userId || faceMatchBusy) return;
		setFaceMatchBusy(true);
		try {
			const updated = await recomputeFaceMatch(userId);
			if (Array.isArray(updated)) {
				setUserPhotos(updated);
			}
		} catch (err) {
			console.error("recomputeFaceMatch failed", err);
		} finally {
			setFaceMatchBusy(false);
		}
	};

	useEffect(() => {
		fetchUsers();
	}, [pageNumber, filterTerm, filterCountry, filterCity, filterSex]);

	const handleExpandClick = (userId) => {
		setExpandedUserId((prev) => {
			const isClosing = prev === userId;

			if (isClosing) {
				// close: без API
				setEditUser(undefined);
				setUserPhotos([]);
				setUserSelfie(undefined);
				setVideoId(0);
				setVideoUrl("");
				setIsPlaying(false);
				setIsMuted(true);
				return null;
			}

			// open: только тут API
			const user = users.find((u) => u.id === userId);
			setEditUser(user);
			setVideoId(0);
			setVideoUrl("");
			setIsPlaying(false);
			setIsMuted(true);

			fetchUserPhotos(userId);
			fetchUserSelfie(userId);
			fetchUserVideo(userId);

			return userId;
		});
	};

	const handleButton = (type) => {
		setUserButtonData({ type, id: 0, userId: expandedUserId });
		if (type === "block") {
			setShowBlock(true);
		} else setIsConfirmPopupOpen(true);
	};

	const toggleBlockModal = () => {
		setShowBlock((prev) => !prev);
	};

	// A deleted profile can still be blocked: deletion frees the account to come
	// back, blocking is what keeps the person out.
	const applyBlock = async () => {
		// Причина обязательна: блокировку без неё потом не объяснить, и бэкенд
		// её отклоняет.
		const blockReason = normalizeBlockReason(reason);
		if (!blockReason) {
			alert(BLOCK_REASON_REQUIRED_TEXT);
			return;
		}
		try {
			await blockUser({ userId: expandedUserId, reason: blockReason });
			removeUserFromLists(expandedUserId);
			setShowBlock(false);
			setReason("");
		} catch (e) {
			console.error(e);
		}
	};

	const removeUserFromLists = (userId) => {
		setUsers((prev) => prev.filter((u) => u.id !== userId));

		if (expandedUserId === userId) {
			setExpandedUserId(null);
			setUserPhotos([]);
			setUserSelfie();
			setVideoId(0);
			setVideoUrl("");
		}
	};
	const hasUnmoderated = ({ photos, selfie, video }) => {
		const photosPending =
			Array.isArray(photos) && photos.some((p) => p?.moderated === false);
		const selfiePending = !!selfie && selfie.moderated === false;
		const videoPending = !!video && video.moderated === false;
		return photosPending || selfiePending || videoPending;
	};

	const refreshUser = async (userId) => {
		if (!userId) return;

		try {
			const [photosResp, selfieResp, videoResp] = await Promise.allSettled([
				getPhotos(userId),
				getSelfie(userId),
				getVideo(userId),
			]);

			// ✅ if any request failed — don’t remove user by mistake
			const canDecide =
				photosResp.status === "fulfilled" &&
				selfieResp.status === "fulfilled" &&
				videoResp.status === "fulfilled";

			const photos =
				photosResp.status === "fulfilled" ? (photosResp.value ?? []) : [];
			const selfie =
				selfieResp.status === "fulfilled" ? selfieResp.value : null;
			const video = videoResp.status === "fulfilled" ? videoResp.value : null;

			// update UI only for expanded user
			if (expandedUserId === userId) {
				setUserPhotos(Array.isArray(photos) ? photos : []);
				setUserSelfie(selfie);

				if (video?.fileName) {
					setVideoId(video.id);
					setVideoUrl(`${filePath}/Video/${userId}/${video.fileName}`);
				} else {
					setVideoId(0);
					setVideoUrl("");
				}
			}

			if (canDecide && !hasUnmoderated({ photos, selfie, video })) {
				removeUserFromLists(userId);
			}
		} catch (e) {
			console.error("refreshUser failed:", e);
		}
	};

	const handleByType = (type, id, userId) => {
		setUserButtonData({ type, id, userId });
		setIsConfirmPopupOpen(true);
	};

	const handleConfirmPopup = async () => {
		if (!userButtonData) return;
		const { type, id, userId } = userButtonData;
		try {
			if (type === "photoDelete") {
				await handleDeletePhoto(id, userId);
			} else if (type === "selfieDelete") {
				await handleDeleteSelfie(id, userId);
			} else if (type === "videoDelete") {
				await handleDeleteVideo(id, userId);
			}
		} catch (error) {
			console.error("Confirm action failed:", error);
		} finally {
			setIsConfirmPopupOpen(false);
		}
	};

	const handleDeletePhoto = async (id, userId) => {
		await deletePhoto(id);
		await refreshUser(userId);
	};

	const handleDeleteSelfie = async (id, userId) => {
		await deleteSelfie(id);
		await refreshUser(userId);
	};

	const handleDeleteVideo = async (id, userId) => {
		await deleteVideo(id);
		await refreshUser(userId);
	};

	const toggleFilterModal = (type) => {
		setIsFilterModalOpen(type);
	};
	const handleFilterRemove = () => {
		setFilterCountry("");
		setFilterCity("");
		setFilterSex(0); // Reset the sex filter (to an invalid value)
		setPageNumber(1);
	};

	const applyFiltersHandler = () => {
		setPageNumber(1);
		toggleFilterModal(false);
	};
	const handleClickOutside = (event) => {
		if (modalRef.current && !modalRef.current.contains(event.target)) {
			setIsFilterModalOpen(false);
		}
	};

	useEffect(() => {
		if (isFilterModalOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}

		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isFilterModalOpen]);

	if (loading) {
		return <div>Загрузка...</div>;
	}

	const closePopup = () => {
		setIsPopupOpen(false);
	};
	const confirmDel = () => {
		setUsers((prevUsers) =>
			prevUsers.filter((user) => user.id !== expandedUserId),
		);

		setIsPopupOpen(false);
	};
	const closeConfirmPopup = () => {
		setIsConfirmPopupOpen(false);
	};

	const handleTogglePlay = () => {
		if (videoRef.current) {
			if (videoRef.current.paused) {
				videoRef.current.play();
				setIsPlaying(true);
			} else {
				videoRef.current.pause();
				setIsPlaying(false);
			}
		}
	};

	const handleToggleMute = () => {
		if (videoRef.current) {
			videoRef.current.muted = !videoRef.current.muted;
			setIsMuted(videoRef.current.muted);
		}
	};

	const handleVideoEnd = () => {
		setIsPlaying(false);
	};
	const makeBigger = () => {
		setBigger(!bigger);
	};
	// Pagination handlers

	const handlePreviousPage = () => {
		if (pageNumber > 1) {
			setPageNumber((prev) => prev - 1);
		}
	};

	const handleNextPage = () => {
		if (pageNumber < totalPages) {
			setPageNumber((prev) => prev + 1);
		}
	};
	const handleSearchChange = (e) => {
		setFilterTerm(e.target.value);
		setPageNumber(1); // Reset to first page when search term changes
	};

	return (
		<div className="AllProfiles">
			{showModal && (
				<PhotoModal photo={userPhoto} onClose={() => setShowModal(false)} />
			)}

			<ConfirmPopup
				isOpen={isConfirmPopupOpen}
				onConfirm={handleConfirmPopup}
				onCancel={closeConfirmPopup}
			/>
			<Popup
				isOpen={isPopupOpen}
				onClose={closePopup}
				editData={editUser}
				onConfirm={confirmDel}
			/>

			<div className="filter-button">
				<button onClick={() => toggleFilterModal(true)}>Фильтровать</button>
				<button onClick={handleFilterRemove}>Удалить фильтры</button>
			</div>
			<div className="search-bar">
				<input
					type="text"
					placeholder="Поиск: id, tg, почта, имя, никнейм"
					value={filterTerm}
					onChange={handleSearchChange}
					className="search-input"
				/>
			</div>

			{showBlock && (
				<div className="filter-modal">
					<div className="filter-modal-content">
						<h3>Заблокировать пользователя?</h3>
						<p>Напишите причину</p>
						<textarea
							id="message"
							className={"textArea"}
							name="message"
							value={reason}
							onChange={(e) => setReason(e.target.value)}
						/>

						{!isBlockReasonValid(reason) && (
							<p className="block-reason-hint">
								{BLOCK_REASON_REQUIRED_TEXT}
							</p>
						)}

						<button
							onClick={applyBlock}
							disabled={!isBlockReasonValid(reason)}
						>
							Заблокировать
						</button>
						<button onClick={toggleBlockModal}>Отмена</button>
					</div>
				</div>
			)}

			{isFilterModalOpen && (
				<div className="filter-modal">
					<div className="filter-modal-content" ref={modalRef}>
						<h3>Фильтр</h3>
						<input
							type="text"
							placeholder="Страна"
							value={filterCountry}
							onChange={(e) => setFilterCountry(e.target.value)}
							className="filter-input"
						/>
						<input
							type="text"
							placeholder="Город"
							value={filterCity}
							onChange={(e) => setFilterCity(e.target.value)}
							className="filter-input"
						/>
						<select
							value={filterSex}
							onChange={(e) => setFilterSex(Number(e.target.value))}
							className="filter-input"
						>
							<option value={0}>Фильтр по полу</option>
							<option value={1}>Мужчина</option>
							<option value={2}>Женщина</option>
						</select>
						<button onClick={applyFiltersHandler}>Приминить</button>
						<button onClick={() => toggleFilterModal(false)}>Отмена</button>
					</div>
				</div>
			)}
			{users.length == 0 ? <div>Нет профилей</div> : ""}
			{users.map((user) => (
				<div key={user.id} className="profile-card">
					<div
						className="profile-summary"
						onClick={() => handleExpandClick(user.id)}
						style={{
							backgroundColor: user.sex === 1 ? "#e6f0ff" : "#fff0f6", // Light blue or light pink
							borderRadius: "5px", // Optional: softens edges
						}}
					>
						<div className="profile-summary-cont">
							<h2>
								{user.fullName} (id: {user.id}{user.legacyUserId ? `, tg: ${user.legacyUserId}` : ''})
							</h2>
							<p> Никнейм: {user.userName}</p>
							<p> Почта: {user.mail || "—"}</p>
							<p> Пол: {user.sex === 1 ? "Мужчина" : "Женщина"}</p>
						</div>
						{/* Плашку рисуем только для настоящих тарифов. Прежнее условие
						    прятало её лишь при -1, а всё остальное объявляло Premium,
						    поэтому заказ инкогнито (тип 4) показывался премиумом. */}
						{(user.type === 0 || user.type === 1) && (
							<p className={`userplan ${user.type === 0 ? "plus" : "premium"}`}>
								{user.type === 0 ? "PLUS" : "PREMIUM"}
							</p>
						)}
					</div>
					{expandedUserId === user.id && (
						<div className="profile-details">
							<div className="details-info">
								<div className="info-text">
									<p>
										Дата рождения: {utcToDate(user.birthday)} (Возраст:{" "}
										{user.age})
									</p>
									<p>Страна: {user.country}</p>
									<p>Город: {user.city}</p>
									{user.wRole && <p>Роль: {t(user.wRole)}</p>}
									{user.profession && <p>Дейтельность: {t(user.profession)}</p>}
									<p>О себе: {user.description}</p>
									<p>Цель знакомства: {t(user.propose)}</p>
									<p>Причина удаления: {user.reason}</p>
									<p>Дата удаления: {utcToLocalDateTime(user.deleteDate)}</p>
									<p>Дата Регистрации: {utcToLocalDateTime(user.cDate)}</p>
									<p>
										Дата Последнего входа:{" "}
										{utcToLocalDateTime(user.lastLoginDate)}
									</p>
									{(user.cntReg > 1 || user.cntDel > 0) && (
										<p>
											{user.cntReg > 1 && `Кол. регистраций: ${user.cntReg} `}
											{user.cntDel > 0 &&
												`${user.sex === 2 ? "Удалилась" : "Удалился"}: ${user.cntDel}`}
										</p>
									)}
								</div>
							</div>
							<div className="photos">
								{userSelfie?.fileName ? (
									<div className="photoslot">
										<div className="photoslot-inner">
											<img
												style={{ border: "5px solid red" }}
												src={`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`}
												onClick={() =>
													handleViewPhoto(
														`${filePath}/Selfie/${user?.id}/${userSelfie.fileName}`,
													)
												}
												alt={`Selfie`}
											/>
											<button
												onClick={() =>
													handleByType(
														"selfieDelete",
														userSelfie.id,
														user.id,
													)
												}
											>
												Удалить
											</button>
										</div>
									</div>
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет селфи</h1>
									</div>
								)}
								{userPhotos.length > 0 ? (
									userPhotos.map((photo, index) => (
										<div key={photo.id} className="photoslot">
											<div className="photoslot-inner" style={{ position: "relative" }}>
											<div title={typeof photo.matchPercent === "number" ? `Совпадение с селфи: ${photo.matchPercent.toFixed(0)}%` : "Лицо не сверено"} style={{ position: "absolute", top: 4, left: 4, padding: "2px 6px", borderRadius: 4, background: typeof photo.matchPercent !== "number" ? "#777" : photo.matchPercent >= 50 ? "#2e7d32" : photo.matchPercent >= 30 ? "#ed6c02" : "#c62828", color: "white", fontSize: 12, fontWeight: 600, zIndex: 1 }}>{typeof photo.matchPercent === "number" ? `${photo.matchPercent.toFixed(0)}%` : "?"}</div>
												<img
													src={`${filePath}/Photo/${user?.id}/${photo.fileName}`}
													onClick={() =>
														handleViewPhoto(
															`${filePath}/Photo/${user?.id}/${photo.fileName}`,
														)
													}
													alt={`Photo ${index + 1}`}
												/>
												<button
													onClick={() =>
														handleByType("photoDelete", photo.id, user.id)
													}
												>
													Удалить
												</button>
											</div>
										</div>
									))
								) : (
									<div>
										<h1 style={{ color: "red" }}>Нет фоток</h1>
									</div>
)}
								<div style={{ width: "100%", marginTop: 8 }}>
									<button type="button" disabled={faceMatchBusy || !userSelfie?.fileName || userPhotos.length === 0} onClick={() => handleRecomputeFaceMatch(user.id)} title={!userSelfie?.fileName ? "Сначала загрузите селфи" : "Сверить лицо селфи с фото"} style={{ padding: "6px 12px" }}>{faceMatchBusy ? "Считаем..." : "Сверить лицо"}</button>
								</div>
							</div>
							{videoUrl && (
								<div className="video-player">
									<video
										ref={videoRef} // ✅ Connect the ref
										autoPlay={false} // ❌ Remove autoplay to control manually
										muted={true} // ✅ Ensure it starts muted
										playsInline
										src={videoUrl}
										onEnded={handleVideoEnd}
										className={bigger ? "video-previewbig" : "video-preview"}
										onClick={makeBigger}
									/>

									<div className="but-div">
										<button
											style={{ background: "blue" }}
											onClick={handleTogglePlay}
										>
											{isPlaying ? "PAUSE" : "PLAY"}
										</button>
										<button
											style={{ background: "orange" }}
											onClick={handleToggleMute}
										>
											{isMuted ? "UNMUTE" : "MUTE"}
										</button>
										<button
											style={{ background: "red" }}
											onClick={() =>
												handleByType("videoDelete", videoId, user.id)
											}
										>
											Удалить
										</button>
									</div>
								</div>
							)}

							<div className="actions">
								<div>
									<button
										style={{ background: "black" }}
										onClick={() => handleButton("block")}
									>
										Заблокировать
									</button>
									<button
										style={{ background: "red" }}
										onClick={() => handleButton("userDelete")}
									>
										Удалить
									</button>
								</div>
							</div>
						</div>
					)}
				</div>
			))}
			<div className="pagination">
				<button onClick={handlePreviousPage} disabled={pageNumber === 1}>
					Назад
				</button>
				<span>
					Страница {pageNumber} из {totalPages}
				</span>
				<button onClick={handleNextPage} disabled={pageNumber === totalPages}>
					Дальше
				</button>
			</div>
		</div>
	);
};

export default DellProfiles;
